import React, { createContext, ReactNode, useContext, useMemo } from "react";
import { Account } from "../../../model/accounting/account/Account.js";
import { AccountItem } from "../../../model/accounting/account/AccountItem.js";
import { findAccount } from "../../../model/accounting/Chart.js";
import { useLoadEffect } from "../../../../react/load/useLoadEffect.js";
import { usePeriod } from "../PeriodProvider.js";
import { getAccountItems } from "../accountingApi.js";

type ViewState = {
  account: Account;
  items: AccountItem[];
  total: number;
  inTotal: number;
  outTotal: number;
};

const ViewContext = createContext<ViewState | undefined>(undefined);

type Props = {
  accountId: string;
  children: ReactNode;
};

/**
 * Account view state provider.
 */
export const ViewProvider = ({ accountId, children }: Props) => {
  const { year, month, titleSearch } = usePeriod();

  const result = useLoadEffect(`account-${accountId}-${year}-${month}`, () =>
    getAccountItems(accountId, year, month)
  );

  const items = useMemo(() => (result.state === "success" ? result.data : []), [result]);

  const filteredItems = useMemo(() => {
    const titleSearchLowerCase = titleSearch.toLowerCase();
    return items.filter((item) => item.entryTitle.toLowerCase().includes(titleSearchLowerCase));
  }, [items, titleSearch]);

  const total = useMemo(() => {
    return filteredItems.reduce((prev, item) => prev + item.change, 0);
  }, [filteredItems]);

  const inItems = useMemo(() => {
    return filteredItems.filter((item) => item.change > 0);
  }, [filteredItems]);

  const inTotal = useMemo(() => {
    return Math.abs(inItems.reduce((prev, item) => prev + item.change, 0));
  }, [inItems]);

  const outItems = useMemo(() => {
    return filteredItems.filter((item) => item.change < 0);
  }, [filteredItems]);

  const outTotal = useMemo(() => {
    return Math.abs(outItems.reduce((prev, item) => prev + item.change, 0));
  }, [outItems]);

  const account = findAccount(accountId);

  const value = { account, items: filteredItems, total, inTotal, outTotal };

  return <ViewContext.Provider value={value}>{children}</ViewContext.Provider>;
};

/**
 * Helper hook to access the account view state.
 */
export const useAccountView = () => {
  const context = useContext(ViewContext);
  if (!context) {
    throw new Error(`Account view context is not provided.`);
  }
  return context;
};
